import React from 'react';
import AgencyNavbar from '../components/agencies/AgencyNavbar';
import Footer from '../components/layout/Footer';
import VideoSlot from '../components/marketing/VideoSlot';
import MoxieEmbed from '../components/agencies/MoxieEmbed';
import PartnerJourneyStep from '../components/agencies/PartnerJourneyStep';
import SafeIcon from '../common/SafeIcon';
import {
  AUTHORITY_BUILDER_LOGIN_URL,
  MOXIE_PARTNER_PORTAL_LOGIN_URL,
} from '../config/partnerAccess';

const ONBOARDING_EMBED_URL =
  'https://hello.withmoxie.com/01/digital-cowboy/repupgrade-partner-onboarding?inFrame=true';
const ONBOARDING_PUBLIC_URL =
  'https://clients.digitalcowboy.io/public/repupgrade-partner-onboarding';
const KICKOFF_URL =
  'https://hello.withmoxie.com/01/digital-cowboy/repupgrade-partner-kickoff?inFrame=true';

export default function PartnerOnboarding() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F7F8F7] font-lexend text-[#101F1F]">
      <AgencyNavbar />

      <main id="main-content" className="px-4 pb-24 pt-32 sm:px-6 lg:px-8 lg:pb-32 lg:pt-40">
        <header className="mx-auto max-w-4xl text-center">
          <p className="font-michroma text-[0.68rem] font-bold uppercase tracking-[0.2em] text-[#409FA1] sm:text-xs">
            RepUpgrade Partner Activation
          </p>
          <h1 className="mt-5 text-balance text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Tell us about your business. Book your kickoff. Launch RepUpgrade.
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-[#244A49]/75">
            This is the next step for established agencies and consultants
            whose Agency Partner Dominate application has been approved and whose agreement is confirmed. Complete these steps after your partner review.
          </p>

          <div className="mx-auto mt-9 grid max-w-3xl grid-cols-3 border-y border-[#244A49]/10 py-5 text-center">
            {['Partner Profile', 'Partner Kickoff', 'Build Authority'].map(
              (label, index) => (
                <div
                  key={label}
                  className={index > 0 ? 'border-l border-[#244A49]/10 px-2' : 'px-2'}
                >
                  <span className="block font-michroma text-[0.6rem] font-bold text-[#409FA1]">
                    0{index + 1}
                  </span>
                  <span className="mt-1 block text-xs font-semibold text-[#244A49] sm:text-sm">
                    {label}
                  </span>
                </div>
              )
            )}
          </div>
        </header>

        <div className="mx-auto mt-12 max-w-4xl"><VideoSlot name="onboarding" /></div>
        <div className="mx-auto mt-16 max-w-6xl">
          <PartnerJourneyStep
            number="01"
            eyebrow="Partner Profile"
            title="Tell Us About Your Business"
            description="Give us the information we need to prepare your RepUpgrade partner account, positioning, first-client targets, and white-label setup."
          >
            <MoxieEmbed
              id="repupgrade-onboarding-form"
              title="RepUpgrade Partner Onboarding Form"
              src={ONBOARDING_EMBED_URL}
              fallbackUrl={ONBOARDING_PUBLIC_URL}
              fallbackLabel="Open Partner Onboarding Form"
              minHeight="750px"
            />
          </PartnerJourneyStep>

          <PartnerJourneyStep
            number="02"
            eyebrow="Partner Kickoff"
            title="Book Your Partner Kickoff"
            description="In your 45-minute kickoff, we'll map your launch, confirm your first 1–3 client targets, set the handoff between your business and RepUpgrade, and get you moving toward your first client going live."
          >
            <MoxieEmbed
              id="repupgrade-partner-kickoff"
              title="RepUpgrade Partner Kickoff Scheduler"
              src={KICKOFF_URL}
              fallbackUrl={KICKOFF_URL}
              fallbackLabel="Open Partner Kickoff Scheduler"
              minHeight="700px"
            />
          </PartnerJourneyStep>

          <PartnerJourneyStep
            number="03"
            eyebrow="Build Authority"
            title="Enter RepUpgrade After Activation"
            description="Check your email for the Moxie portal invitation or magic link. Then open the RepUpgrade Partner Portal. Partner academy access will be shared separately when it is ready. The client login below opens the product, not the academy."
            isLast
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap">
              <a
                href={MOXIE_PARTNER_PORTAL_LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#F49558] px-6 py-3 font-bold text-[#101F1F] transition hover:bg-[#D35E0E]"
              >
                Open the RepUpgrade Partner Portal
                <SafeIcon name="ArrowUpRight" className="h-4 w-4" />
              </a>
              <a
                href={AUTHORITY_BUILDER_LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-12 items-center gap-2 rounded-xl border border-[#244A49]/20 bg-white px-6 py-3 font-bold text-[#244A49] transition hover:border-[#409FA1] hover:text-[#409FA1]"
              >
                Client login
                <SafeIcon name="ArrowUpRight" className="h-4 w-4" />
              </a>
            </div>
          </PartnerJourneyStep>
        </div>
      </main>

      <Footer />
    </div>
  );
}